import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { useAdminAuth } from '../utils/adminAuth';
import { useCapability } from '../utils/capabilityContext';
import WorkspaceFrame from '../components/WorkspaceFrame';
import './AuthPages.css';

const AdminLogin = () => {
  const [adminToken, setAdminToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAdminToken, setShowAdminToken] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isAdminAuthenticated, loading: authLoading, loginAdmin } = useAdminAuth();
  const { refresh: refreshCapabilities } = useCapability();
  const redirectTo = location.state?.from?.pathname || '/admin';

  useEffect(() => {
    if (authLoading || !isAdminAuthenticated) {
      return undefined;
    }

    let cancelled = false;
    const finishAuthentication = async () => {
      try {
        await refreshCapabilities();
      } catch (_error) {
        // Capability loading is best-effort and must not block an authenticated session.
      }
      if (!cancelled) {
        navigate(redirectTo, { replace: true });
      }
    };
    finishAuthentication();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAdminAuthenticated, navigate, refreshCapabilities, redirectTo]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      await loginAdmin(adminToken);
    } catch (requestError) {
      console.error('管理员登录失败:', requestError);
      setError(t('dashboard.adminAuthFailed'));
      setLoading(false);
    }
  };

  const checkingAccess = authLoading || isAdminAuthenticated;
  const tokenToggleLabel = showAdminToken ? t('auth.hideAdminToken') : t('auth.showAdminToken');
  const TokenVisibilityIcon = showAdminToken ? EyeOff : Eye;

  return (
    <WorkspaceFrame className="workspace-frame--auth">
      <main className="auth-page auth-page--admin" aria-labelledby="admin-login-title">
        <Link to="/" className="auth-page__back">
          <ArrowLeft className="auth-page__back-icon" size={18} aria-hidden="true" />
          <span>{t('auth.backToWorkspaces')}</span>
        </Link>

        <section className="auth-page__surface">
          <div className="auth-page__role">
            <span className="auth-page__role-icon" aria-hidden="true">
              <ShieldCheck size={24} strokeWidth={1.8} />
            </span>
            <span className="auth-page__role-label">{t('dashboard.adminArea')}</span>
          </div>

          <header className="auth-page__header">
            <h1 id="admin-login-title" className="auth-page__title">{t('dashboard.adminAuthorization')}</h1>
            <p className="auth-page__description">{t('dashboard.adminAuthRequired')}</p>
          </header>

          <div className="auth-page__content">
            {checkingAccess ? (
              <div className="auth-page__loading" role="status" aria-live="polite">
                <div className="loading-orb" aria-hidden="true" />
                <span className="auth-page__loading-title">{t('dashboard.verifying')}</span>
              </div>
            ) : (
              <>
                {error && (
                  <div className="status-banner status-banner--error" role="alert">
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="auth-page__form stack" aria-busy={loading || undefined}>
                  <div className="field">
                    <label htmlFor="admin-token" className="field__label">
                      {t('dashboard.adminToken')}
                    </label>
                    <div className="auth-secret-field">
                      <input
                        id="admin-token"
                        type={showAdminToken ? 'text' : 'password'}
                        className="theme-input auth-secret-field__input"
                        value={adminToken}
                        onChange={(event) => setAdminToken(event.target.value)}
                        placeholder={t('dashboard.enterAdminToken')}
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        className="auth-secret-field__toggle"
                        onClick={() => setShowAdminToken((visible) => !visible)}
                        aria-label={tokenToggleLabel}
                        aria-pressed={showAdminToken}
                        title={tokenToggleLabel}
                      >
                        <TokenVisibilityIcon size={19} strokeWidth={2} aria-hidden="true" />
                      </button>
                    </div>
                    <span className="field__hint">{t('dashboard.adminLoginHint')}</span>
                  </div>

                  <div className="auth-page__actions">
                    <button
                      type="submit"
                      className="theme-button theme-button--primary theme-button--full"
                      disabled={loading || isAdminAuthenticated}
                    >
                      {loading ? t('dashboard.verifying') : t('dashboard.verify')}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </section>
      </main>
    </WorkspaceFrame>
  );
};

export default AdminLogin;
